import { Link } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/hooks/useNotifications';
import { useLocale } from '@/contexts/LocaleContext';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

export function NotificationsBell() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const { data: notifications = [], unreadCount, markAllRead, markRead } = useNotifications();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full relative"
          aria-label={isArabic ? 'الإشعارات' : 'Notifications'}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-popover">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold text-sm">{isArabic ? 'الإشعارات' : 'Notifications'}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => markAllRead()}
            >
              <Check className="w-3 h-3" />
              {isArabic ? 'تحديد الكل كمقروء' : 'Mark all read'}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {notifications.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {isArabic ? 'لا توجد إشعارات' : 'No notifications'}
            </p>
          ) : (
            <ul className="divide-y">
              {notifications.map((n) => {
                const title = isArabic ? n.title_ar : (n.title_en || n.title_ar);
                const body = isArabic ? n.body_ar : (n.body_en || n.body_ar);
                const content = (
                  <div className={cn('p-3 hover:bg-accent transition-colors cursor-pointer', !n.is_read && 'bg-primary/5')}>
                    <div className="flex items-start gap-2">
                      {!n.is_read && (
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-tight">{title}</p>
                        {body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{body}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatDistanceToNow(new Date(n.created_at), {
                            addSuffix: true,
                            locale: isArabic ? ar : enUS,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <li key={n.id} onClick={() => !n.is_read && markRead(n.id)}>
                    <Link to={n.link || '/dashboard/notifications'}>{content}</Link>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
        <div className="p-2 border-t">
          <Button variant="ghost" size="sm" className="w-full text-xs" asChild>
            <Link to="/dashboard/notifications">
              {isArabic ? 'عرض كل الإشعارات' : 'View all notifications'}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
